import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import type {
  IRowNode,
  SelectionChangedEvent,
  RowClassParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import type { AgGridReactProps } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { PrimaryContext } from '@/services/context';
import {
  isOnFarmImplantEmbryoActionable,
  type OnFarmImplantSessionEmbryo,
  onFarmImplantSessionEmbryosOptions,
} from '@/services/on-farm-implant';
import { useContextMenu } from '@/utils/context-menu';

import { AddToQueueDialog } from './AddToQueueDialog';
import { useFrozenEmbryoColumnDefs } from './columnDefs';

type FrozenTabProps = {
  sessionId: string | null;
  readOnly?: boolean;
};

export const FrozenTab = ({ sessionId, readOnly = false }: FrozenTabProps) => {
  const { t } = useTranslation();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems } = useContextMenu({ idKey: 'sessionEmbryoId' });
  const gridRef = useRef<AgGridReact>(null);

  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [queueDialogOpen, setQueueDialogOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    ...onFarmImplantSessionEmbryosOptions({
      sessionId: sessionId ?? '',
      sourceType: 'frozen',
    }),
    enabled: !!sessionId,
  });

  const columnDefs = useFrozenEmbryoColumnDefs(readOnly);

  const rows = useMemo(() => data?.items ?? [], [data]);

  const selectableCount = useMemo(
    () => rows.filter(isOnFarmImplantEmbryoActionable).length,
    [rows],
  );

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      setSelectedIds(
        (event.api.getSelectedRows() as OnFarmImplantSessionEmbryo[]).map(
          (row) => row.sessionEmbryoId,
        ),
      );
    },
    [],
  );

  const handleClearSelection = useCallback(() => {
    gridRef.current?.api.deselectAll();
  }, []);

  const selectedEmbryos = useMemo(() => {
    const ids = new Set(selectedIds);
    return rows.filter((row) => ids.has(row.sessionEmbryoId));
  }, [rows, selectedIds]);

  const handleQueued = useCallback(() => {
    gridRef.current?.api.deselectAll();
  }, []);

  const isRowSelectable = useCallback(
    (node: IRowNode) =>
      !readOnly &&
      !!node.data &&
      isOnFarmImplantEmbryoActionable(node.data as OnFarmImplantSessionEmbryo),
    [readOnly],
  );

  const getRowClass = useCallback(
    (params: RowClassParams) =>
      params.data &&
      !isOnFarmImplantEmbryoActionable(
        params.data as OnFarmImplantSessionEmbryo,
      )
        ? 'opacity-60'
        : undefined,
    [],
  );

  if (!sessionId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-snowflake mb-3 text-4xl" />
        <p className="text-sm">{t('onFarmImplant.frozen.noSession')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 py-24 text-sm text-gray-500">
        <i className="pi pi-spin pi-spinner text-sm" />
        {t('onFarmImplant.frozen.loading')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-gray-500">
        <i className="pi pi-exclamation-triangle text-3xl text-amber-500" />
        <p className="text-sm">{t('onFarmImplant.frozen.loadError')}</p>
        <Button
          onClick={() => {
            void refetch();
          }}
        >
          {t('onFarmImplant.frozen.retry')}
        </Button>
      </div>
    );
  }

  if (!data?.sourceSelected) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-snowflake mb-3 text-4xl" />
        <p className="text-base font-medium">
          {t('onFarmImplant.frozen.notLoadedTitle')}
        </p>
        <p className="mt-1 text-sm">
          {t('onFarmImplant.frozen.notLoadedBody')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative w-56">
            <i className="pi pi-search absolute top-1/2 left-2 -translate-y-1/2 text-xs text-gray-400" />
            <Input
              placeholder={t('onFarmImplant.frozen.filterPlaceholder')}
              className="h-7 pl-7 text-xs"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.frozen.embryoCount', {
              count: data.totalRecords,
            })}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.frozen.selectedCount', {
              selected: selectedIds.length,
              total: selectableCount,
            })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={selectedIds.length === 0}
            onClick={handleClearSelection}
          >
            {t('onFarmImplant.frozen.clearSelection')}
          </Button>
          <Button
            size="sm"
            disabled={readOnly || selectedIds.length === 0}
            onClick={() => setQueueDialogOpen(true)}
          >
            {t('onFarmImplant.frozen.queue.addToQueue')}
          </Button>
        </div>
      </div>

      <div
        className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-[calc(100vh-13rem)] min-h-[400px] w-full`}
      >
        <WithLocalStorage storageKey="on-farm-implant-frozen-tab">
          <AgGridReact
            ref={gridRef}
            gridOptions={gridOptions}
            columnDefs={columnDefs as AgGridReactProps['columnDefs']}
            defaultColDef={defaultColDef}
            rowData={rows}
            headerHeight={36}
            rowHeight={48}
            rowSelection="multiple"
            suppressRowClickSelection
            isRowSelectable={isRowSelectable}
            getRowClass={getRowClass}
            quickFilterText={searchText}
            getRowId={getRowId<OnFarmImplantSessionEmbryo>(
              (params) => params.data.sessionEmbryoId,
            )}
            getContextMenuItems={getContextMenuItems}
            onSelectionChanged={handleSelectionChanged}
          />
        </WithLocalStorage>
      </div>

      <AddToQueueDialog
        open={queueDialogOpen}
        onOpenChange={setQueueDialogOpen}
        sessionId={sessionId}
        embryos={selectedEmbryos}
        onQueued={handleQueued}
      />
    </div>
  );
};
